import { type BambuserProductModel, BambuserGetProducts as GetProducts } from 'autogen/swagger/all';
import searchProducts from './searchProducts';
import type { BambuserProduct } from './shared/types';

async function getBambuserProducts(ref: BambuserProduct['ref']): Promise<BambuserProductModel> {
    // Gets a list of products
    const products = (await searchProducts(ref)).products.items;
    // Take first product's id (has up to 10)
    const productId = products.flat().map((product) => product.id)[0];
    // Take first bambuserProduct (has only one, is just returned as array somehow)
    const bambuserProducts = await GetProducts({ productId: [productId] });
    return bambuserProducts[0];
}

export default async function provideProductDataFunction(bambuserProduct: BambuserProduct, oneToOneEmbed: any) {
    const { id: bambuserId, ref, type } = bambuserProduct;
    if (type === 'url') return; // TODO

    await getBambuserProducts(ref).then((product) => {
        const { description, name, productId, variants } = product;
        oneToOneEmbed.updateProduct(bambuserId, (productFactory) =>
            productFactory

                // Mandatory
                // currency of the product
                .currency('DKK')

                // Mandatory
                // locale that product is localized to
                // Bambuser follow  ISO 639-1 Code + "-" + ISO 3166-2 as standard locale format.
                // The value should be of type string
                .locale('da-DK')

                // Mandatory
                // The product method contains a new chain
                // these are methods specific to this actual product
                .product((detailFactory) =>
                    detailFactory

                        // Optional
                        // Name of the product
                        .name(name)

                        // Optional
                        // Sku of your product
                        .sku(productId)

                        // Optional
                        // Define the index of specific variation
                        // which will be selected by default when
                        // agent fetches a product

                        // Example:
                        // .defaultVariationIndex(yourMethodToGetVariationIndex(ref, type))
                        .defaultVariationIndex(0)

                        // Mandatory
                        // Description for the product
                        .description(description)

                        // Preferred
                        // URL for product
                        // .url(window.location.origin + slug)

                        // Mandatory
                        // The attributes method contains a new chain
                        // returned array defines any dimensions (e.g. color, size,...) of your product
                        /*  .attributes((attribute) =>
                            options.map((attr) =>
                                // Mandatory if your products contain more than one variant
                                // Attribute identifier
                                attribute(attr.optionId)
                                    // Optional - If not provided, above specifed attribute identifier will replace it
                                    // Localized name of the attribute
                                    // This will show as a dropdown label in agent tool
                                    .name(attr.name)

                                    // Mandatory
                                    // The options method contains a new chain
                                    // Returned array available options of the dimension
                                    .options((option) =>
                                        attr.values.map((optionName) =>
                                            // Mandatory
                                            // Option identifier
                                            option(optionName)
                                                // Optional
                                                // If not provided, above specifed option identifier will replace it
                                                // Localized name of the option
                                                // This will show as an option inside a dropdown in agent tool
                                                .name(optionName)
                                        )
                                    )
                            )
                        ) */

                        // Mandatory
                        // The variations method contains a new chain
                        // Returned array defines variants (e.g. colors) of your product
                        .variations((variationFactory) =>
                            variants.map(
                                (variation) =>
                                    // Mandatory
                                    // The variationFactory method contains a new chain
                                    // These are methods specific to this actual product variation
                                    variationFactory()
                                        // Mandatory
                                        // Name of the variation
                                        .name(`${name} - ${variation.variantName}`)

                                        // Preferred
                                        // URL for variation
                                        // .url(window.location.origin + variation.slug)

                                        // Optional
                                        // Displayed below the price of the product
                                        // .subtitle(brand)

                                        // Mandatory
                                        // sku (or any other identifier for your product)
                                        // specific down to this variation
                                        .sku(variation.productId.toString())

                                        // Optional
                                        // Set whether this variation is in stock
                                        // if false, the product wont be able to get added to the cart
                                        // and it will get "Sold out" label when shown in the call
                                        .inStock(variation.inStock)

                                        // Mandatory
                                        // list of image urls for the variation
                                        // ordered the same as you want it displayed
                                        //
                                        // NOTE:
                                        // Provided images will be compressed to optimize call performance
                                        // If your product images are loading slowly / not loading, you might
                                        // have to whitelist our Image Transformer.
                                        // Read more about this in FAQ section under Provide Product Data:
                                        // https://bambuser.com/docs/one-to-one/provide-product-data#frequently-asked-questions
                                        .imageUrls(variation.imageUrls)

                                        // Optional
                                        // The price method contains a new chain
                                        // Defines price for the specific variation
                                        .price((priceFactory) =>
                                            priceFactory

                                                // Optional
                                                // original price (in case current is a sale price)
                                                .original(variation.priceBefore)

                                                // Mandatory
                                                // current price of the product
                                                .current(variation.price)
                                        )

                                // Mandatory if your products contain more than one variant
                                // The attributes method contains a new chain
                                // Returned array defines attributes for the unique variation
                                /* .attributes((attribute) =>
                                    options.map((attr) =>
                                        // Mandatory
                                        // Attribute identifier and option identifier that identifies the variation
                                        attribute(attr.optionId, variation[`option${attr.optionId}`])
                                    )
                                ) */

                                // Optional
                                // List of extra specifications to show when comparing products.
                                // These will show on top of already specified attributes above
                                // .comparableAttributes((attribute) =>
                                //     comparableAttributes.map((compAttr) =>
                                //         // Mandatory inside comparableAttributes()
                                //         // Comparable Attribute identifier and its value
                                //         attribute(compAttr.comparableAttributesId, compAttr.value)
                                //             // Optional
                                //             // If not provided, the above specifed attribute identifier will replace it
                                //             // Localized name of the comparable attribute
                                //             // This will show as an attribute name when comparing products
                                //             .name(compAttr.name)
                                //     )
                                // )

                                // Optional
                                // List of related products
                                // that will be shown in agent product information view
                                // .relatedProducts((relatedProductFactory) =>
                                //     relatedProducts.map((relatProd) =>
                                //         // Mandatory inside relatedProducts()
                                //         // The relatedProductFactory method contains a new chain
                                //         // These are methods specific to this actual related product
                                //         relatedProductFactory()
                                //             // Mandatory inside relatedProductFactory()
                                //             // Title of the related product
                                //             .title(relatProd.title)

                                //             // Mandatory inside relatedProductFactory()
                                //             // SKU of the related product
                                //             // If agent clicks on the related product, the "provide-product-data" event
                                //             // will get triggered and pass this specific SKU as a reference
                                //             .sku(relatProd.sku)

                                //             // Mandatory inside relatedProductFactory()
                                //             // string of the thumbnail image url for the related product
                                //             .imageUrl(relatProd.thumbnail)

                                //             // Mandatory inside relatedProductFactory()
                                //             // The price method contains a new chain
                                //             // Defines price for the specific variation
                                //             .price((priceFactory) =>
                                //                 priceFactory
                                //                     // Mandatory inside priceFactory
                                //                     // current price of the product
                                //                     .current(relatProd.price)
                                //             )
                                //     )
                                // )

                                // Optional
                                // Unique selling points of the variant or product
                                // displayed on the product display page

                                // Options:
                                //    BULLETS - expect an array
                                //    PARAGRAPH - expects a string
                                // .details((detail) => [
                                //     detail(
                                //         'BULLETS',
                                //         details.map((details) => details)
                                //     )
                                // ])

                                // Optional
                                // The rating method contains a new chain
                                // Defines rating for the specific variant or product
                                /// Rating are displayed as stars
                                // .rating((ratingFactory) =>
                                //     ratingFactory

                                //         // Mandatory inside ratingFactory
                                //         // Specify number max value
                                //         // This equal to the amount of total stars
                                //         // For best UI experience we do not recommend exceeding 10
                                //         .maxValue(rating.maxValue)

                                //         // Mandatory inside ratingFactory
                                //         // Average rating of your product
                                //         // This equals to how many amount of total stars are filled
                                //         // Should be equal or lower than the maxValue
                                //         .rating(rating.averageRating)

                                //         // Optional
                                //         // Number rating of your variant or product
                                //         // Displayed next to the star ratings
                                //         .numberOfRatings(rating.numberOfRatings)
                                // )
                            )
                        )
                )
        );
    });
}
